import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ElementType,
  type ReactNode,
} from "react";

type Variant = "up" | "zoom" | "left" | "right";

// SSR-safe layout effect
const useIso = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export function Reveal({
  children,
  variant = "up",
  delay = 0,
  className = "",
  as: As = "div",
  once = true,
}: {
  children: ReactNode;
  variant?: Variant;
  delay?: number;
  className?: string;
  as?: ElementType;
  once?: boolean;
}) {
  const ref = useRef<HTMLElement | null>(null);
  // Default to visible — prevents above-the-fold flicker.
  // We flip to false synchronously before paint only if the element
  // is below the viewport, so off-screen items still animate in.
  const [visible, setVisible] = useState(true);

  useIso(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const inView = rect.top < window.innerHeight - 60 && rect.bottom > 0;
    if (!inView) {
      setVisible(false);
    }
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    // Safety net: always reveal within 1.5s in case observer never fires.
    const safety = window.setTimeout(() => setVisible(true), 1500);
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return () => window.clearTimeout(safety);
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) {
            io.disconnect();
          }
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold: 0.05, rootMargin: "0px 0px 80px 0px" }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      window.clearTimeout(safety);
    };
  }, [once]);

  const variantClass =
    variant === "zoom"
      ? "reveal-zoom"
      : variant === "left"
        ? "reveal-left"
        : variant === "right"
          ? "reveal-right"
          : "reveal";

  return (
    <As
      ref={ref as never}
      data-visible={visible ? "true" : "false"}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      className={`${variantClass} ${className}`}
    >
      {children}
    </As>
  );
}

export function CountUp({
  to,
  duration = 1600,
  suffix = "",
  className = "",
}: {
  to: number;
  duration?: number;
  suffix?: string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started.current) {
          return;
        }
        started.current = true;
        const start = performance.now();
        const tick = (now: number) => {
          const p = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - p, 3);
          if (ref.current) {
            ref.current.textContent = `${Math.round(to * eased).toLocaleString("en-IN")}${suffix}`;
          }
          if (p < 1) {
            requestAnimationFrame(tick);
          }
        };
        requestAnimationFrame(tick);
        io.disconnect();
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [to, duration, suffix]);

  return (
    <span ref={ref} className={className}>
      0{suffix}
    </span>
  );
}
